import React from 'react';
import { Heart, Target, Eye, CheckCircle } from 'lucide-react';
import { aboutContent } from '../data/mock';

const HERO_IMAGE = "https://images.unsplash.com/photo-1658490250803-c7daa6e5967f?w=1920&q=80";

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section" style={{ minHeight: '60vh' }}>
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="hero-overlay" />
        <div className="absolute inset-0 z-2 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '400px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="hero-content container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-white mb-6 tracking-widest">About Us</h1>
            <p className="text-white/70 text-lg tracking-wide">
              Fatimia Community Services Society of BC — A Farsi Speaking Afghan Shi'a Muslim Centre
            </p>
            <div className="islamic-divider mt-8">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="gold-border-left">
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Our Story</p>
              <h2 className="heading-3 mb-6">About Our Centre</h2>
              <p className="text-[#5a6d62] text-lg leading-relaxed">
                {aboutContent.aboutUs}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section section-alt pattern-overlay">
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid-2">
            <div className="card card-gold">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center">
                  <Target className="w-7 h-7 text-[#c5a572]" />
                </div>
                <h2 className="text-2xl text-[#1a2f23]">Our Mission</h2>
              </div>
              <p className="text-[#5a6d62] leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>

            <div className="card card-gold">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 border border-[#c5a572] flex items-center justify-center">
                  <Eye className="w-7 h-7 text-[#c5a572]" />
                </div>
                <h2 className="text-2xl text-[#1a2f23]">Our Vision</h2>
              </div>
              <p className="text-[#5a6d62] leading-relaxed">
                {aboutContent.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">What We Stand For</p>
            <h2 className="heading-2 mb-6">Our Core Values</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {aboutContent.values.map((value, index) => (
              <div key={`value-${value.slice(0,20)}`} className="card group">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 border border-[#c5a572]/30 group-hover:border-[#c5a572] group-hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300">
                    <CheckCircle className="w-5 h-5 text-[#c5a572]" />
                  </div>
                  <p className="text-[#1a2f23] group-hover:text-[#c5a572] transition-colors">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shi'a Identity Section */}
      <section className="section-dark py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60')`,
          backgroundSize: '300px',
          backgroundRepeat: 'repeat'
        }} />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="islamic-divider mb-8">
              <div className="islamic-star" />
            </div>
            <h2 className="text-4xl text-white mb-8">Our Faith</h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">
              As a Shi'a Ithna Asheri Muslim community, we follow the teachings of the Holy Quran, 
              the Prophet Muhammad (PBUH), and the Twelve Imams of the Ahlul Bayt (AS). 
              Our center is a place where we come together to worship, learn, and strengthen our connection 
              to Allah (SWT) and His chosen guides.
            </p>
            <div className="quote-box bg-white/5 border-[#c5a572] backdrop-blur-sm">
              <p className="text-white/80 text-lg italic leading-relaxed">
                "Indeed, Allah intends only to remove from you the impurity [of sin], O people of the 
                [Prophet's] household, and to purify you with [extensive] purification."
              </p>
              <p className="text-[#c5a572] mt-4 font-display text-lg">— Quran 33:33</p>
            </div>
            <div className="islamic-divider mt-12">
              <div className="islamic-star" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
